"use client"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { User, Calendar, MapPin, FileText, AlertTriangle, Clock, Shield, ExternalLink, Download } from "lucide-react"
import type { SuspectDetail } from "@/lib/interfaces"

interface SuspectDetailPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  suspect: SuspectDetail | null
}

export function SuspectDetailPopup({ open, onOpenChange, suspect }: SuspectDetailPopupProps) {
  if (!suspect) return null

  const getRiskLevel = (count: number) => {
    if (count >= 3) return { level: "High Risk", color: "bg-red-100 text-red-800", icon: AlertTriangle }
    if (count === 2) return { level: "Medium Risk", color: "bg-orange-100 text-orange-800", icon: AlertTriangle }
    return { level: "Low Risk", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const riskInfo = getRiskLevel(suspect.count)
  const RiskIcon = riskInfo.icon

  const exportSuspectData = () => {
    const suspectData = {
      name: suspect.name,
      fatherName: suspect.fatherName,
      totalCases: suspect.count,
      riskLevel: riskInfo.level,
      cases: suspect.generalDiaries.map((gd) => ({
        gdNumber: gd.gdNo,
        date: formatDate(gd.gdDateTime),
        caseType: gd.caseType,
        policeStation: gd.policeStation,
        officerName: gd.officerName,
        subject: gd.subject,
      })),
    }

    const dataStr = JSON.stringify(suspectData, null, 2)
    const dataBlob = new Blob([dataStr], { type: "application/json" })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement("a")
    link.href = url
    link.download = `suspect-${suspect.name.replace(/\s+/g, "-")}-details.json`
    link.click()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] !max-w-6xl h-[90vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold text-black flex items-center gap-2">
              <User className="h-5 w-5" />
              Suspect Profile: {suspect.name}
            </DialogTitle>
            <Button onClick={exportSuspectData} size="sm" className="bg-black text-white hover:bg-gray-800 mr-16">
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 min-h-0">
          <div className="space-y-6 pb-4">
            {/* Suspect Overview */}
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Suspect Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Name</p>
                    <p className="text-black font-semibold">{suspect.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Father's Name</p>
                    <p className="text-black">
                      {suspect.fatherName || <span className="italic text-gray-400">Not specified</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Cases</p>
                    <p className="text-black font-semibold">{suspect.count}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Risk Assessment</p>
                    <Badge className={`${riskInfo.color} hover:${riskInfo.color}`}>
                      <RiskIcon className="h-3 w-3 mr-1" />
                      {riskInfo.level}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Case Timeline */}
            <Tabs defaultValue="timeline" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                <TabsTrigger value="timeline" className="data-[state=active]:bg-white">
                  <Clock className="h-4 w-4 mr-2" />
                  Case Timeline
                </TabsTrigger>
                <TabsTrigger value="details" className="data-[state=active]:bg-white">
                  <FileText className="h-4 w-4 mr-2" />
                  Detailed Cases
                </TabsTrigger>
              </TabsList>

              <TabsContent value="timeline">
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-black">Case Timeline ({suspect.count} cases)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {suspect.generalDiaries
                        .sort((a, b) => new Date(b.gdDateTime).getTime() - new Date(a.gdDateTime).getTime())
                        .map((gd, index) => (
                          <div key={gd.id} className="flex gap-4 p-4 border border-gray-200 rounded-lg">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-black">GD No: {gd.gdNo}</h4>
                                <Badge variant="outline">{gd.caseType}</Badge>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div className="flex items-center gap-1 text-gray-600">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(gd.gdDateTime)}
                                </div>
                                <div className="flex items-center gap-1 text-gray-600">
                                  <MapPin className="h-3 w-3" />
                                  {gd.policeStation?.name}
                                </div>
                                <div className="flex items-center gap-1 text-gray-600">
                                  <User className="h-3 w-3" />
                                  {gd.officerName || "Not specified"}
                                </div>
                              </div>
                              <p className="text-sm text-gray-700 line-clamp-2">{gd.subject}</p>
                              {gd.underSection && (
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {gd.underSection}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="details">
                <div className="space-y-4">
                  {suspect.generalDiaries
                    .sort((a, b) => new Date(b.gdDateTime).getTime() - new Date(a.gdDateTime).getTime())
                    .map((gd, index) => (
                      <Card key={gd.id} className="border border-gray-200">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg text-black flex items-center gap-2">
                              <FileText className="h-5 w-5" />
                              Case #{index + 1} - GD No: {gd.gdNo}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{gd.gdType}</Badge>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Basic Case Information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Date & Time</p>
                              <p className="text-black">{formatDate(gd.gdDateTime)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Case Type</p>
                              <p className="text-black">{gd.caseType}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Police Station</p>
                              <p className="text-black">{gd.policeStation?.name}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">District</p>
                              <p className="text-black">{gd.districtUnit}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">State</p>
                              <p className="text-black">{gd.state}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Officer</p>
                              <p className="text-black">{gd.officerName || "Not specified"}</p>
                            </div>
                          </div>

                          <Separator />

                          {/* Subject */}
                          <div>
                            <p className="text-sm font-medium text-gray-600 mb-2">Subject</p>
                            <p className="text-black bg-gray-50 p-3 rounded-lg">{gd.subject}</p>
                          </div>

                          {/* Sections */}
                          {gd.underSection && (
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-2">Under Section</p>
                              <Badge variant="outline">{gd.underSection}</Badge>
                            </div>
                          )}

                          {/* Incident Description */}
                          <div>
                            <p className="text-sm font-medium text-gray-600 mb-2">Incident Description</p>
                            <div className="bg-gray-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                              <p className="text-black text-sm whitespace-pre-wrap">{gd.incidentDescription}</p>
                            </div>
                          </div>

                          {/* Action Taken */}
                          {gd.actionTaken && gd.actionTaken !== "NA" && (
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-2">Action Taken</p>
                              <div className="bg-blue-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                                <p className="text-black text-sm whitespace-pre-wrap">{gd.actionTaken}</p>
                              </div>
                            </div>
                          )}

                          {/* Report Information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Report Printed On</p>
                              <p className="text-black">{formatDateOnly(gd.reportPrintedOn)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Printed By</p>
                              <p className="text-black">
                                {gd.reportPrintedByName} ({gd.reportPrintedByRank})
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end pt-4 border-t border-gray-200 bg-white">
          <Button onClick={() => onOpenChange(false)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
